import {useCallback} from "react";
import {useImages} from "contexts/images";
import {useSetting} from "contexts/setting";
import {useBgTransformContext} from "contexts/bgTransform";

export interface BannerCrop {
  x: number;
  y: number;
  scale: number;
  rotate: number;
  flip: {x: boolean; y: boolean};
}

/** The images-context writers this hook uses. The context itself is untyped JS. */
interface CropWriters {
  setUserBgAdjustment: (bgOption: unknown, templateId: string, crop: BannerCrop) => void;
  clearUserBgAdjustment: (bgOption: unknown, templateId: string) => void;
  editBgX: (templateId: string, value: number | undefined) => void;
  editBgY: (templateId: string, value: number | undefined) => void;
  editBgScale: (templateId: string, value: number | undefined) => void;
  editBgRotate: (templateId: string, value: number | undefined) => void;
  editBgFlip: (templateId: string, value: BannerCrop["flip"] | undefined) => void;
}

/**
 * Writes a banner's background crop where the banner reads it, for code that
 * gives a banner a new image (an upload, a pick for a selection) or takes it
 * away. BgTransformer's commitBgTransform makes the same choice for the
 * banner's own tools.
 *
 * With bgDerivation on, the displayed crop is derived, and the user intent for
 * (bgOption, banner) is its top layer. The context records are not read
 * there, and the default scene's crop for the banner, made for another image,
 * would win. On a saved scene's route (derivation off) the context records are
 * the state.
 *
 * `derived` tells the caller which of the two it writes.
 */
export function useBannerCrop() {
  const {enabled: derived} = useBgTransformContext();
  const {bgOption} = useSetting() as {bgOption: unknown};
  const images = useImages() as unknown as CropWriters;
  const {
    setUserBgAdjustment,
    clearUserBgAdjustment,
    editBgX,
    editBgY,
    editBgScale,
    editBgRotate,
    editBgFlip,
  } = images;

  const setCrop = useCallback(
    (templateId: string, crop: BannerCrop) => {
      if (derived) {
        setUserBgAdjustment(bgOption, templateId, crop);
        return;
      }
      editBgX(templateId, crop.x);
      editBgY(templateId, crop.y);
      editBgScale(templateId, crop.scale);
      editBgRotate(templateId, crop.rotate);
      editBgFlip(templateId, crop.flip);
    },
    [
      derived,
      bgOption,
      setUserBgAdjustment,
      editBgX,
      editBgY,
      editBgScale,
      editBgRotate,
      editBgFlip,
    ]
  );

  /**
   * Drop the crop the banner's own image had. With derivation the banner then
   * shows the derived crop of the image it falls back to.
   */
  const dropCrop = useCallback(
    (templateId: string) => {
      if (derived) {
        clearUserBgAdjustment(bgOption, templateId);
        return;
      }
      editBgX(templateId, undefined);
      editBgY(templateId, undefined);
      editBgScale(templateId, undefined);
      editBgRotate(templateId, undefined);
      editBgFlip(templateId, undefined);
    },
    [
      derived,
      bgOption,
      clearUserBgAdjustment,
      editBgX,
      editBgY,
      editBgScale,
      editBgRotate,
      editBgFlip,
    ]
  );

  return {derived, setCrop, dropCrop};
}
